import { useRef, useState, useEffect, useCallback } from 'react';
import { useUsuario } from '../../context/usuarioContext';
import { useParams, useNavigate} from 'react-router-dom';
// Componentes UI
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Knob } from 'primereact/knob';
import { AutoComplete } from 'primereact/autocomplete';

// Componentes
import FirmaPad from '../../components/firma-pad/firma-pad.js';
import EstaSeguro from "../../components/alertas/esta-seguro.js";
import MultiSelectDropdown from '../../components/multiselect-dropdown/multiselect-dropdown.js';
import Continuar from '../../components/alertas/continuar.js';
import Permiso from '../../components/wrappers/permiso';
import ModuleLayout from '../../layouts/layout-modulo.js';
// Datos
import { obtenerRevision, enviarRevision, 
        actualizarRevision, agregarRevisionVehiculo, 
        obtenerMecanicos, obtenerServicios } from '../../services/mecanicaService.js';
import { obtenerVehiculoByPatente, buscarPatentes } from '../../services/vehiculosService';
// Hooks y validaciones
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import useFormDraft from '../../hooks/useFormDraft';
import { mantencionSchema } from '../../validation/addMantencionValidation';        
// Utils
import { generarPDF } from "../../utils/pdf/pdfInformeMantencion.js";
import { muchasABase64 } from "../../utils/convertBase64.js"
import { toTitleCase, toSentenceCase, toLowerSafe, toUpperSafe } from '../../utils/textFormatter';
import partesRevision from '../../json/revision-tallermc.json';      
// Estilos y Assets
import logo from "../../assets/logos/TRANSKYRIOS-_IconColor.avif";
import "./informe-mantencion.css";

// evitar recreación en cada render
const UNIDADES = ['un', 'pc', 'L', 'ml', 'cc', 'kg', 'g', 'm', 'cm'];
const EMPRESAS = ['Transkyrios', 'Servikyrios'];

// Funciones auxiliares de construcción de datos
const buildClienteData = (data) => ({
  nombre: toTitleCase(data.cliente) || '',
  rut: data.rut || '',
  correo: toLowerSafe(data.correo) || '',
  celular: data.celular || ''
});

const buildVehiculoData = (data) => ({
  fechaIngreso: data.fechaIngreso || '',
  fechaSalida: data.fechaSalida || '',
  patente: toUpperSafe(data.patente) || '',
  marca: toTitleCase(data.marca) || '',
  modelo: toTitleCase(data.modelo) || '',
  anio: data.anio || '',
  nmotor: toUpperSafe(data.nmotor) || '',
  nserie: toUpperSafe(data.nserie) || '',
  kilometrajeAct: data.kilometrajeAct || 0,
  kilometrajeProx: data.kilometrajeProx || 0,
  combustible: data.combustible || 0,
  sintomas: toSentenceCase(data.sintomas) || ''
});

const buildServicioData = (data, repuestosFiltrados) => ({
  tipo: Array.isArray(data.servicio) ? data.servicio.join(', ') : '',
  mecanico: data.mecanico || '',
  mecanico2: data.mecanico2 || '',
  obsMecanico: toSentenceCase(data.obsmecanico) || '',
  observaciones: toSentenceCase(data.observaciones) || '',
  diagnostico: toSentenceCase(data.diagnostico) || '',
  recomendacion: toSentenceCase(data.recomendacion) || '',
  repuestos: repuestosFiltrados,
  ultimaReparacion: toSentenceCase(data.ultimaReparacion) || '',
  empresa: toSentenceCase(data.empresa) || '',
});

const buildEvaluaciones = (data) =>
  Object.entries(data.evaluaciones || {})
    .filter(([, estado]) => estado !== null && estado !== undefined)
    .map(([id, estado]) => {
      const parte = partesRevision.find(p => Number(p.id) === Number(id));
      return { id: String(id), nombre: parte ? parte.nombre : 'Parte desconocida', estado };
});

const buildRepuestos = (data) =>
  (data.repuestos || [])
    .filter(r => r.nombre && r.nombre.trim() !== '')
    .map(r => ({
      nombre: toTitleCase(r.nombre.trim()),
      cantidad: r.cantidad || 1,
      unidad: r.unidad || 'un' 
}));

function InformeMantencion() {
  const toast = useRef(null);
  const navigate = useNavigate();
  const { numeroOT } = useParams();
  const { usuario, tienePermiso } = useUsuario();
  const [ evidencias, setEvidencias ] = useState([]);
  const [ mecanicos, setMecanicos ] = useState([]);
  const [ servicios, setServicios ] = useState([]);
  const [ escaners, setEscaners ] = useState([]);
  const [ confirmVisible, setConfirmVisible ] = useState(false);
  const [ isSubmitting, setIsSubmitting ] = useState(false);
  const [patentes, setPatentes] = useState([]);

  // Firmas
  const sigCliente = useRef(null);
  const sigMecanico = useRef(null);

  // Modo
  const esEdicion = Boolean(numeroOT);

  // Permisos
  const esRevisor = tienePermiso('Taller Mecánico', 'MEC_REVISOR_INFORMES');
  const puedeEnviar = tienePermiso('Taller Mecánico', 'MEC_ENVIARCORREO_INFORME');

  const { register, handleSubmit, formState: { errors }, getValues, control, reset, watch, setValue } = useForm({
    resolver: yupResolver(mantencionSchema),
      context: { sigCliente, sigMecanico },
      defaultValues: { 
        mecanico: `${usuario?.nombre || ''} ${usuario?.apellido_paterno || ''}`.trim(), 
        combustible: 0, 
        repuestos: [{ nombre: '', cantidad: '', unidad: 'un' }], 
        servicio: [],
        empresa: ''
      },
      mode: 'onTouched', 
      reValidateMode: "onChange",
  });

  // Identidades para la clave del borrador
  const formId = 'informe-mantencion';
  const userId = usuario?.id || usuario?.rut || usuario?.correo || 'anon';
  const modeId = numeroOT ? `${numeroOT}` : 'nuevo';

  // Hook de autosave (excluye campos pesados)
  const { restorePrompt, restore, discard, isOnline, clearDraft } = useFormDraft({
    formId, userId, modeId, watch, getValues, reset, toast, 
    exclude: [ 'evidencias', 'escaners', 'firmas' ], 
    enabled: !esEdicion, askOnMount: !esEdicion
  });

  // Control lista de repuestos
  const { fields, append, remove } = useFieldArray({ control, name: 'repuestos' });

  // Función auxiliar para mostrar toasts de error con el mensaje del service/backend
  const showError = (summary, detail, life = 4500) => {
    toast.current.show({ severity:'error', summary, detail, life });
  };

  // Obtener listado de mecánicos
  const getMecanicos = useCallback(async () => {
    try {
      const response = await obtenerMecanicos();
      const lista = Array.isArray(response) ? response.map(m => m.nombre) : [];
      setMecanicos(lista);
    } catch (error) {
      showError('Error al cargar mecánicos', typeof error === 'string' ? error : 'No se pudo obtener el listado de mecánicos. Recarga la página.');
    }
  }, []);

    // Obtener listado de servicios ofrecidos por el taller
  const getServicios = useCallback(async () => {
    try {
      const response = await obtenerServicios();
      const lista = Array.isArray(response) ? response.map(m => m.nombre) : [];
      setServicios(lista);
    } catch (error) {
      showError('Error al cargar servicios', typeof error === 'string' ? error : 'No se pudo obtener el listado de servicios. Recarga la página.');
    }
  }, []);

  // Para modo edición
  const getInforme = useCallback(async (ot) => {
    try {
      const data = await obtenerRevision(ot);
      reset({
        mecanico: data.mecanico_nombre || '',
        mecanico2: data.mecanico2_nombre || '',
        servicio: data.servicio_tipo
          ? String(data.servicio_tipo).split(',').map(s => s.trim()).filter(Boolean)
          : [],
        empresa: data.empresa || '',
        fechaIngreso: data.fechaIngreso ? data.fechaIngreso.split('T')[0] : '',
        fechaSalida: data.fechaSalida ? data.fechaSalida.split('T')[0] : '',
        patente: toUpperSafe(data.patente) || '',
        marca: toTitleCase(data.marca) || '',
        modelo: toTitleCase(data.modelo) || '',
        anio: data.anio || '',
        nserie: toUpperSafe(data.nserie) || '',
        nmotor: toUpperSafe(data.nmotor) || '',
        kilometrajeAct: data.kilometrajeAct || 0,
        kilometrajeProx: data.kilometrajeProx || 0,
        combustible: data.combustible || 0,
        sintomas: toSentenceCase(data.sintomas) || '',
        cliente: toTitleCase(data.cliente_nombre) || '',
        rut: toUpperSafe(data.cliente_rut) || '',
        correo: toLowerSafe(data.cliente_correo) || '',
        celular: data.cliente_celular || '',
        observaciones: toSentenceCase(data.observaciones) || '',
        diagnostico: toSentenceCase(data.diagnostico) || '',
        recomendacion: toSentenceCase(data.recomendacion) || '',
        repuestos: Array.isArray(data.repuestos)
          ? data.repuestos.map(rep => ({
              nombre: rep.nombre || '',
              cantidad: rep.cantidad || '',
              unidad: rep.unidad || 'un'
            }))
          : [],
        obsmecanico: toSentenceCase(data.obsMecanico) || '',
        ultimaReparacion: toSentenceCase(data.ultimaReparacion) || '',
        evaluaciones: data.evaluaciones?.reduce((acc, item) => {
          acc[item.id] = item.estado;
          return acc;
        }, {}) || {}
      });
    } catch (error) {
      showError(
        `Error al cargar informe N°OT ${ot}`,
        typeof error === 'string' ? error : `No se pudo cargar el informe N°OT ${ot}. Verifica tu conexión e intenta nuevamente.`
      );
    }
  }, [reset]);

  // Entra en modo edición si encuentra numeroOT
  useEffect(() => {
    const cargarDatos = async () => {
      // Servicios y Vehiculos siempre activo
      await getServicios();
      if (!numeroOT) return;
      // Modo edición
      if (esRevisor) await getMecanicos();
      await getInforme(numeroOT);
    };
    cargarDatos();
  }, [numeroOT, esRevisor, getInforme, getMecanicos, getServicios]);

  const buscarPatente = async (eventoAutocomplete) => {
    const patenteBuscada = eventoAutocomplete?.query?.toUpperCase().trim();
    
    if (patenteBuscada.length < 2) {
      setPatentes([]);
      return;
    }

    try {
      const vehiculos = await buscarPatentes(patenteBuscada);
      setPatentes(vehiculos || []);
    } catch (e) { 
      setPatentes([]); 
    } finally {
    }
  };
  
  const seleccionarVehiculo = async (e) => {
    try {
      const patente = e.patente;
      const vehiculo = await obtenerVehiculoByPatente(patente);
      const v = vehiculo;

      if (!v) return;

      setValue("patente",     v.patente, { shouldDirty: true });
      setValue("id_vehiculo", v.id_vehiculo || "", { shouldDirty: true });
      setValue("marca",       v.marca      || "", { shouldDirty: true });
      setValue("modelo",      v.modelo      || "", { shouldDirty: true });
      setValue("anio",        v.anio        || '', { shouldDirty: true });
      setValue("nserie",      v.nserie      || '', { shouldDirty: true });
      setValue("nmotor",      v.nmotor      || '', { shouldDirty: true });
      setValue("kilometrajeAct",  v.kmActual ?? 0, { shouldDirty: true });
      setValue("kilometrajeProx", v.kmActual ?? 0,  { shouldDirty: true });

    } catch (e) {
      showError('Error', 'No se pudo obtener el vehículo.');
      throw e;
    }
  }

  // Limpiar formulario 
  const limpiarForm = () => {
    reset({
      mecanico: `${usuario?.nombre || ''} ${usuario?.apellido_paterno || ''}`.trim(),
      combustible: 0,
      repuestos: [{ nombre: '', cantidad: '', unidad: 'un' }],
      servicio: [], empresa: '',
      patente: '', marca: '',  modelo: '', anio: '', nserie: '', nmotor: '',
      kilometrajeAct: 0, kilometrajeProx: 0,
    });
    sigMecanico.current?.clear();
    sigCliente.current?.clear();
    setEvidencias([]);
  }

  // Evidencias
  const handleEvidencias = (e) => {
    const MAX_SIZE_MB = 5;
    const files = Array.from(e.target.files);
    const muyGrande = files.find(file => file.size > MAX_SIZE_MB * 1024 * 1024);
    
    if (muyGrande) {
      toast.current.show({
        severity: 'error', summary: 'Archivo muy grande', life: 3500,
        detail: `El archivo ${muyGrande.name} supera ${MAX_SIZE_MB} MB.`
      });
      e.target.value = '';
      return;
    }

    setEvidencias(files);
  };

  // Escaners
  const handleEscaners = (e) => {
    const files = Array.from(e.target.files);

    if (files.length > 2) {
      toast.current.show({
        severity: 'warn', summary: 'Advertencia', life: 3500, 
        detail: 'Solo puedes subir un máximo de 2 archivos PDF.'
      });
      e.target.value = '';
      return;
    }

    for (let file of files) {
      if (file.type !== "application/pdf") {
        toast.current.show({
          severity: 'error', summary: 'Error', life: 3500,
          detail: `El archivo ${file.name} no es un PDF.`
        });
        e.target.value = "";
        return;
      }
    }

    setEscaners(files);
  };

  // Obtener datos para envío
  const getDatos = async () => {
    const data = getValues();
    
    let imagenesEvidencia = [];
    try {
      imagenesEvidencia = await muchasABase64(evidencias);
    } catch (e) {
      showError('Error en imágenes', 'No se pudieron procesar las imágenes de evidencia. Intenta quitarlas y volver a agregarlas.');
      throw e;
    }

    let imgCliente = null;
    let imgMecanico = null;
    try {
      imgCliente  = sigCliente.current.isEmpty()  ? null : sigCliente.current.toDataURL();
      imgMecanico = sigMecanico.current.isEmpty() ? null : sigMecanico.current.toDataURL();
    } catch (e) {
      showError('Error en firmas', 'No se pudieron leer las firmas. Limpia y vuelve a firmar.');
      throw e;
    }

    const evaluacionesFiltradas = buildEvaluaciones(data);
    const repuestosFiltrados    = buildRepuestos(data);

    return {
      cliente:     buildClienteData(data),
      vehiculo:    buildVehiculoData(data),
      servicio:    buildServicioData(data, repuestosFiltrados),
      evaluaciones: evaluacionesFiltradas,
      evidencias:  imagenesEvidencia,
      firmas: { cliente: imgCliente, mecanico: imgMecanico }
    };
  }

  // Obtiene docDefinition del documento y permite mostrarlo en distintos formatos
  const crearDocumento = async (accion, datos = null) => {
    let data;
    try { data = datos || await getDatos(); } 
    catch (e) { throw e; }

    let pdf;
    try { pdf = generarPDF(data); }
    catch (e) {
      showError('Error al generar PDF', 'No se pudo generar el documento PDF. Intenta nuevamente.');
      throw e;
    }

    const nombreArchivo = data.numeroOT ? `${data.numeroOT}.pdf` : 'revision-vehiculo.pdf';
    
    try {
      switch (accion) {
        case 'ver':
          pdf.open();
          break;
        case 'descargar':
          pdf.download(nombreArchivo);
          break;
        case 'blob':
          return new Promise((resolve, reject) => {
            pdf.getBlob((blob) => {
              if (!blob) {
                showError('Error al generar PDF', 'El PDF se generó vacío. Intenta nuevamente.');
                reject(new Error('Blob vacío'));
              } else {
                resolve({ blob, data });
              }
            });
          });
        default: 
          pdf.open();
          break;
      }
    } catch (e) {
      showError('Error al procesar PDF', `No se pudo ${accion === 'ver' ? 'abrir' : 'descargar'} el documento. Intenta nuevamente.`);
      throw e;
    }
  }

  // Ver ¿Esta seguro?
  const enviarYGuardar = () => { setConfirmVisible(true); };
  // Sí, Esta seguro. -> Enviar E-mail
  const confirmarEnvio = async () => {
    setConfirmVisible(false);

    try {
      const datos = await guardarDatos();
      if (!datos) return;
      
      let blob, data;
      try { ({ blob, data } = await crearDocumento('blob', datos)); } 
      catch (e) { return; } // crearDocumento ya mostró el toast
      
      const formData = new FormData();
      formData.append('documentoOT', blob, `OT-${data.numeroOT}.pdf`); // Adjunto: PDF OT
      escaners?.forEach((file) => formData.append('escaners', file, file.name)); // Adjunto: Escaners (si existen)
      // Datos para el contenido del correo
      formData.append('correoCliente', data.cliente.correo        || '--');
      formData.append('nombreCliente', data.cliente.nombre        || '--');
      formData.append('patente',       data.vehiculo.patente      || '--');
      formData.append('marca',         data.vehiculo.marca        || '--');
      formData.append('modelo',        data.vehiculo.modelo       || '--');
      formData.append('fechaIngreso',  data.vehiculo.fechaIngreso || '--');
      formData.append('fechaSalida',   data.vehiculo.fechaSalida  || '--');

      let response;
      try { 
        response = await enviarRevision(datos.numeroOT, formData); 
      } catch (error) {
        showError(
          'Error al enviar correo',
          typeof error === 'string' ? error : 'No se pudo enviar el correo. Verifica que el cliente tenga un correo válido.'
        );
        return;
      }

      toast.current.show({ 
        severity: response.status === 200 ? 'success' : 'error', 
        summary: response.status === 200 ? 'Correo enviado' : 'Error al enviar correo',
        detail: response.msg,
        life: 4500
      });

      // Al enviar correctamente, limpia borrador local
      if (response.status === 200) {
        clearDraft();
        try {
          await crearDocumento('descargar', datos);
          toast.current.show({
            severity: 'success', summary: 'Copia descargada',
            detail: 'Se descargó una copia del documento automáticamente en su dispositivo.',
            life: 4500
          });
        } catch (e) {
          // El correo ya se envió. Este error es menor, solo informa.
          toast.current.show({ 
            severity: 'info', summary: 'Aviso', life: 4500,
            detail: 'El correo fue enviado pero no se pudo descargar la copia local del PDF.'
          });
        }
      }
    } catch (error) {
      showError(
        'Error inesperado',
        typeof error === 'string' ? error : 'Ocurrió un error inesperado durante el envío. Intenta nuevamente.'
      );
    }
  };

  // Guarda los datos en bd
  const guardarDatos = async () => {
    setIsSubmitting(true);

    try {
      let datos;
      try { datos = await getDatos(); } 
      catch (e) { return; } // getDatos ya mostró el toast específico
    
      if (!datos.firmas.cliente || !datos.firmas.mecanico) {
        toast.current.show({ severity: 'warn', summary: 'Firmas requeridas', detail: 'Debe firmar tanto el cliente como el mecánico antes de guardar.', life: 3500});
        return;
      } 
      if (!datos.firmas.cliente) {
        toast.current.show({ severity: 'warn', summary: 'Firmas requeridas', detail: 'Falta la firma del cliente. Pídele que firme antes de continuar.', life: 3500});
        return;
      } 
      if (!datos.firmas.mecanico) {
        toast.current.show({ severity: 'warn', summary: 'Firmas requeridas', detail: 'Falta la firma del mecánico. Debe firmar antes de continuar.', life: 3500});
        return;
      }
    
      let documento;
      try {
        if (numeroOT) { // Modo Edición
          documento = await actualizarRevision(numeroOT, {
            cliente: datos.cliente, 
            vehiculo: datos.vehiculo, 
            servicio: datos.servicio, 
            evaluaciones: datos.evaluaciones
          });
        } else { // Modo Creación 
          documento = await agregarRevisionVehiculo({ 
            cliente: datos.cliente, 
            vehiculo: datos.vehiculo, 
            servicio: datos.servicio, 
            evaluaciones: datos.evaluaciones
          });
        }
      } catch (error) {
        showError(
          numeroOT ? 'Error al actualizar' : 'Error al guardar',
          typeof error === 'string' ? error : `No se pudo ${numeroOT ? 'actualizar' : 'guardar'} el informe. Verifica tu conexión e intenta nuevamente.`
        );
        return;
      }  

      toast.current.show({
        severity: 'success', summary: 'Guardado',
        detail: numeroOT ? 'Informe actualizado correctamente.' : 'Informe guardado correctamente.',
        life: 4500
      });

      // Retornar datos para PDF oficial
      if (documento.numeroOT) {
        datos.numeroOT = documento.numeroOT?.['@numeroOT'] ?? documento.numeroOT ?? numeroOT;
        if(!numeroOT) limpiarForm();
        return datos;
      } else {
        // El servidor respondió OK pero sin numeroOT — algo salió mal en el proc. almacenado
        toast.current.show({ 
          severity: 'info', summary: 'Aviso', life: 4500,
          detail: 'El informe se guardó pero no se pudo obtener el número de OT. Recarga la página para verificar.'
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const soloGuardar = async () => {
    const datos = await guardarDatos();
    if (datos) { 
      try {
        await crearDocumento('ver', datos);
      } catch (e) {} // crearDocumento ya mostró el toast, el guardado fue exitoso así que no alarmar más
    }
  }

  return (
    <ModuleLayout titulo="Taller Mecánico" logo={logo} onVolver={() => navigate('/app/taller')}>
      <Toast ref={toast} position="center"/>
      
      {!esEdicion && !isOnline && (
        <div className="alert alert-warning py-2 px-3 mb-3" role="alert">
          Estás sin conexión. Tu progreso se guardará localmente y podrás continuar al volver.
        </div>
      )}
      
      <section className="tmc-content">
        <div className="tmc-banner">
          <h3>
             { esEdicion ? `Editar: Recepción Vehicular ${numeroOT}` : 'Crear Informe: Recepción Vehicular' }
          </h3>
          <p className='mb-0'>
             { esEdicion 
              ? `Está en modo edición. Si fue un error, haga clic en [ 🡠 ] para regresar.`
              : `Completa el siguiente formulario para generar un informe detallado de la 
              mantención realizada a un vehículo en el taller.` }
          </p>
        </div>
        
        <section className="tmc-form">
          <form className="container-form">
            {/* Mecánico */}
            <div className='card-nc'>
              <h5>Información general del servicio</h5>
              {/* Empresa que realiza el servicio */}
              <div className="form-comprobante-row">
                <div className="mb-2 form-comprobante-field">
                  <label className="form-label">Empresa que realiza el servicio<span className="required">*</span></label>
                    <select 
                      {...register('empresa')}
                      className={`form-select ${errors.empresa ? 'is-invalid' : ''}`}
                    >
                      <option value="">Seleccione empresa</option>
                      {EMPRESAS.map( e => (
                        <option key={e} value={e}>
                          {e}
                        </option> 
                      ))}
                    </select>
                  {errors.empresa && ( <div className="invalid-feedback d-block"> {errors.empresa.message} </div> )}
                </div>
              </div>

              {/* Mecánico, Segundo Mecánico y Servicio Realizado */}
              <div className="form-comprobante-row">
                {/* Mecánico a cargo del servicio */}
                <div className="mb-2 form-comprobante-field">
                  <label className="form-label">Mecánico<span className="required">*</span></label>
                  <input type="text" className='form-control' {...register("mecanico")} readOnly/>
                </div>

                {/* Aparece campo segundo mecanico solo si es REVISOR y si esta en modo EDICION */}
                {(esRevisor && esEdicion && (mecanicos != null)) ? (
                  <div className="mb-2 form-comprobante-field">
                    <label className="form-label">Segundo Mecánico</label>
                      <select {...register('mecanico2')} className='form-select'>
                        <option value="">Seleccionar mecánico</option>
                        {mecanicos.map((meca) => (<option key={meca} value={meca}>{meca}</option> ))}
                      </select>
                  </div>
                ) : null}

                {/* Seleccion de servicios realizados */}
                <div className="mb-2 form-comprobante-field">
                  <label className="form-label">
                    Servicio<span className="required">*</span>
                  </label>
                  <Controller
                    control={control}
                    name="servicio"
                    render={({ field }) => (
                      <MultiSelectDropdown
                        options={servicios}
                        value={field.value}
                        onChange={field.onChange}  
                        onBlur={field.onBlur}      
                        buttonClassName={`w-100 multiselect-dropdown ${errors.servicio ? "is-invalid" : ""}`}
                      />
                    )}
                  />
                  {errors.servicio && ( <div className="invalid-feedback d-block"> {errors.servicio.message} </div> )}
                </div>
              </div>
            </div>

            {/* Vehículo */}
            <div className='card-nc'>
              <h5>Información del vehículo</h5>
              {/* Fechas ingreso - salida */}
              <div className="form-comprobante-row">
                <div className="mb-2 form-comprobante-field">
                  <label className="form-label">Fecha de Ingreso<span className="required">*</span></label>
                  <input type='date' className={`form-control ${errors.fechaIngreso ? 'is-invalid' : ''}`} {...register('fechaIngreso')}/>
                  {errors.fechaIngreso && ( <div className="invalid-feedback">{errors.fechaIngreso.message}</div>)}
                </div>
                <div className="mb-2 form-comprobante-field">
                  <label className="form-label">Fecha de Salida<span className="required">*</span></label>
                  <input type='date' className={`form-control ${errors.fechaSalida ? 'is-invalid' : ''}`} {...register('fechaSalida')}/>
                  {errors.fechaSalida && ( <div className="invalid-feedback">{errors.fechaSalida.message}</div>)}
                </div>
              </div>
  
              <div className="form-comprobante-row">
                {/* PATENTE */}
                <div className="mb-2 form-comprobante-field">
                  <label className="form-label">Patente<span className="required">*</span></label>

                  <Controller
                    name="patente"
                    control={control}
                    className= {'w-100'}
                    render={({ field, fieldState }) => (
                      <div>
                        <AutoComplete
                          className="w-100"
                          placeholder="Ingrese patente"
                          value={field.value}
                          suggestions={patentes}
                          completeMethod={buscarPatente}
                          field="patente"
                          itemTemplate={(item) => item.patente}
                          onChange={(e) => { field.onChange(
                              typeof e.value === 'string'
                                  ? e.value.toUpperCase()
                                  : e.value.patente
                          ); }}
                          onBlur={field.onBlur}
                          onSelect={(e) => { seleccionarVehiculo(e.value); }}
                          inputClassName={`form-control text-uppercase ${
                            fieldState.error ? 'is-invalid' : ''
                          }`}
                          pt={{input: { className: `form-control`}}}
                        />
                        {/* Error en el input */}
                        {fieldState.error && (
                          <div className="invalid-feedback d-block">
                            {fieldState.error.message}
                          </div>
                        )}
                      </div>
                    )}
                  />
                </div>

                <div className="mb-2 form-comprobante-field">
                  <label className="form-label">Marca<span className="required">*</span></label>
                  <input type="text" placeholder="Marca" 
                    className={`form-control ${errors.marca ? 'is-invalid' : ''}`} {...register('marca')}
                    style={{ textTransform: 'capitalize' }}
                  /> {errors.marca && <div className="invalid-feedback">{errors.marca.message}</div>}
                </div>
              </div>

              <div className="form-comprobante-row">
                <div className="mb-2 form-comprobante-field">
                  <label className="form-label">Modelo<span className="required">*</span></label>
                  <input type="text" placeholder="Modelo"
                    className={`form-control ${errors.modelo ? 'is-invalid' : ''}`} {...register('modelo')}
                    style={{ textTransform: 'capitalize' }}
                  /> {errors.modelo && <div className="invalid-feedback">{errors.modelo.message}</div>}
                </div>

                <div className="mb-2 form-comprobante-field">
                  <label className="form-label">Año<span className="required">*</span></label>
                  <input type="number" placeholder="Año"
                    className={`form-control ${errors.anio ? 'is-invalid' : ''}`} {...register('anio')}
                  /> {errors.anio && <div className="invalid-feedback">{errors.anio.message}</div>}
                </div>
              </div>

              <div className="form-comprobante-row">
                <div className="mb-2 form-comprobante-field">
                  <label className="form-label">Número de Serie</label>
                  <input type="text" placeholder="Número de Serie"
                    className={`form-control ${errors.nserie ? 'is-invalid' : ''}`} {...register('nserie')}
                    style={{ textTransform: 'uppercase' }}
                  /> {errors.nserie && <div className="invalid-feedback">{errors.nserie.message}</div>}
                </div>
                
                <div className="mb-2 form-comprobante-field">
                  <label className="form-label">Número de Motor</label>
                  <input type="text" placeholder="Número de Motor"
                    className={`form-control ${errors.nmotor ? 'is-invalid' : ''}`} {...register('nmotor')}
                    style={{ textTransform: 'uppercase' }}
                  /> {errors.nmotor && <div className="invalid-feedback">{errors.nmotor.message}</div>}
                </div>
              </div>

              <div className="form-comprobante-row">
                <div className="mb-2 form-comprobante-field">
                  <label className="form-label kilometraje-font">Kilometraje - Actual<span className="required">*</span></label>
                  <input type="number" placeholder="Actual" max={999999} min={0}
                    className={`form-control ${errors.kilometrajeAct ? 'is-invalid' : ''}`} {...register('kilometrajeAct')}
                  /> {errors.kilometrajeAct && <div className="invalid-feedback">{errors.kilometrajeAct.message}</div>}

                  <label className="mt-2 form-label kilometraje-font">Kilometraje - Próxima Mantención<span className="required">*</span></label>
                  <input type="number" placeholder="Próxima mantención" max={999999} min={0}
                    className={`form-control ${errors.kilometrajeProx ? 'is-invalid' : ''}`} {...register('kilometrajeProx')}
                  /> {errors.kilometrajeProx && <div className="invalid-feedback">{errors.kilometrajeProx.message}</div>}
                </div>

                <div className="mb-2 form-comprobante-field">
                  <label className="form-label">Nivel de Combustible</label>
                  <div className="card knob-container">
                    <Controller name="combustible" control={control}
                      render={({ field }) => (
                        <Knob value={field.value} step={10} 
                          onChange={(e) => field.onChange(e.value)}
                          valueTemplate="{value}%" />
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-3 form-comprobante-row">
                <div className="mb-1 form-comprobante-field">
                  <label className="form-label">Síntoma Vehicular</label>
                  <textarea 
                    type="text" rows={6}
                    placeholder="Describa los síntomas que presenta el vehículo. Ej: ruidos al frenar, dificultad al arrancar..."
                    className='form-control' {...register('sintomas')}/>
                </div>
              </div>
            </div>

            {/* Informe de Daños */}
            <div className='card-nc'>
              <h5>Revisión de daños</h5>
              <div className="radio-list-container">
                <details open>
                  <summary className="text-start">Ver listado de partes</summary>
                  <div className="card">
                    <table cellPadding="8" cellSpacing="0">
                      <thead>
                        <tr className="text-center">
                          <th className="text-start">Parte</th>
                          <th> ✅ Bueno</th>
                          <th> ⚠️ Averiado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {partesRevision.map((parte) => (
                          <tr key={parte.id}>
                            <td>{parte.nombre}</td>

                            {/* ✅ Bueno */}
                            <td className="text-center">
                              <input
                                className='form-check-input'
                                type="checkbox"
                                checked={watch(`evaluaciones.${parte.id}`) === "Bueno"}
                                onChange={() => {
                                  const currentValue = watch(`evaluaciones.${parte.id}`);
                                  // Si ya estaba marcado, lo desmarca
                                  if (currentValue === "Bueno") {
                                    setValue(`evaluaciones.${parte.id}`, undefined);
                                  } else {
                                    setValue(`evaluaciones.${parte.id}`, "Bueno");
                                  }
                                }}
                              />
                            </td>

                            {/* ⚠️ Averiado */}
                            <td className="text-center">
                              <input
                                className='form-check-input'
                                type="checkbox"
                                checked={watch(`evaluaciones.${parte.id}`) === "Averiado"}
                                onChange={() => {
                                  const currentValue = watch(`evaluaciones.${parte.id}`);
                                  if (currentValue === "Averiado") {
                                    setValue(`evaluaciones.${parte.id}`, undefined);
                                  } else {
                                    setValue(`evaluaciones.${parte.id}`, "Averiado");
                                  }
                                }}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              </div>

              <div className="mb-2 mt-3 form-comprobante-field">
                <label className="form-label">Observaciones Generales</label>
                <textarea 
                  type="text" rows={6}
                  placeholder="Anote cualquier observación relevante sobre el estado general del vehículo.&#10;Ej: Arañazos en la puerta, abolladuras leves, limpieza interior necesaria."
                  className={`form-control ${errors.observaciones ? 'is-invalid' : ''}`} 
                  {...register('observaciones')}
                />
              </div>
            </div>

            {/* Diagnostico y Seguimiento Técnico */}
            <div className='card-nc'>
              <h5>Diagnóstico y seguimiento técnico</h5>
                <label className="form-label m-1">Diagnóstico Preliminar</label>
                <textarea 
                  rows={4} type="text" 
                  placeholder="Ingrese el diagnóstico inicial basado en la inspección. Ej: Falla en el sistema de frenos, desgaste de pastillas."
                  className={`form-control ${errors.diagnostico ? 'is-invalid' : ''}`} 
                  {...register('diagnostico')}
                />
                <label className="form-label m-1">Última reparación registrada</label>
                <textarea 
                    rows={4} type="text" 
                    placeholder="Opcional: detalle de la última reparación realizada. Ej: Cambio de aceite, sustitución de pastillas de freno."
                    className={`form-control ${errors.ultimaReparacion ? 'is-invalid' : ''}`} 
                    {...register('ultimaReparacion')}
                />
                <label className="form-label m-1">Trabajo recomendado a realizar</label>
                <textarea 
                  rows={4} type="text" 
                  placeholder="Describa los trabajos sugeridos para el vehículo. Ej: Ajuste de suspensión, reemplazo de bujías."
                  className={`form-control ${errors.recomendacion ? 'is-invalid' : ''}`} 
                  {...register('recomendacion')}
                />
                <label className="form-label m-1">Observaciones del mecánico</label>
                <textarea 
                  rows={4} type="text" 
                  placeholder="Notas del mecánico sobre inspección o hallazgos. Ej: Se recomienda revisión en 1000 km, no se detectaron fugas."
                  className={`form-control ${errors.obsmecanico ? 'is-invalid' : ''}`}
                  {...register('obsmecanico')}
                />
            </div>

            {/* Repuestos */}
            <div className='card-nc'>
              <h5>Repuestos utilizados</h5>
              <div className="mt-3">
                {fields.map((item, index) => (
                  <div key={item.id} className="repuesto-row">
                    <div className="repuesto-nombre">
                      <label className="mb-0 form-label">Repuesto</label>
                      <input
                        placeholder="Nombre"
                        className='form-control'
                        {...register(`repuestos.${index}.nombre`)}
                      />
                    </div>
                    <div className="repuesto-subgrupo">
                      <div className="repuesto-cantidad">
                        <label className="mb-0 form-label">Cantidad</label>
                        <input
                          className='form-control'
                          placeholder="0" type="number" min="1" step="1"
                          {...register(`repuestos.${index}.cantidad`)}
                        />
                      </div>
                      <div className="repuesto-unidad">
                        <label className="mb-0 form-label">Unidad</label>
                        <select className="form-select" defaultValue="un" {...register(`repuestos.${index}.unidad`)}>
                          {UNIDADES.map((unidad) => (
                            <option key={unidad} value={unidad}>{unidad}</option>
                          ))}
                        </select>
                      </div>

                      <div className="repuesto-eliminar">
                        <Button
                          type="button"
                          className="inv-btn-restar"
                          icon="pi pi-times"
                          tooltip="Eliminar"
                          tooltipOptions={{ position: 'top' }}
                          onClick={() => remove(index)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <div className="mt-4 text-center">
                  <Button type="button"
                    icon="pi pi-plus" 
                    className="btn-editar"
                    label="Agregar Repuesto"
                    onClick={() => append({ nombre: '', cantidad: '0', unidad: 'un' })}
                  />
                </div>
              </div>
            </div>
            
            {/* Escaners */}
            <div className='card-nc'>
              <h5>Adjuntar Escaner (PDF)</h5>
              <div className="form-comprobante-row">
                <small className="text-muted mb-2">
                  Los archivos que suba se adjuntan al correo electronico automatico y no se almacenan en el sistema.
                  <br/>Se recomienda adjuntar los documentos solo cuando se quiera hacer un envio oficial del documento por correo.
                </small>
                <div className="mb-2 form-comprobante-field">
                  <label className="form-label mb-0">Subir o arrastrar archivos.<br/>
                    <small className="text-muted">Solo archivos PDF, máximo 2 archivos.</small>
                  </label>
                  
                  <input type="file" name="escaners" multiple 
                    accept=".pdf" 
                    className="form-control"
                    onChange={handleEscaners}
                  />
                </div>
              </div>
            </div>

            {/* Evidencia */}
            <div className='card-nc'>
              <h5>Evidencia fotografica</h5>
              <div className="form-comprobante-row">
                <small className="text-muted mb-2">
                  Las imagenes solo se adjuntan al documento PDF oficial y no se almacenan en el sistema. 
                  <br/>Se recomienda adjuntar las imagenes solo cuando se quiera hacer una descarga o envio oficial del documento por correo.
                </small>
                <div className="mb-2 form-comprobante-field">
                  <label className="form-label">Subir o arrastrar imágenes</label>
                  <input type="file" name="evidencias" multiple 
                    accept="image/*" 
                    className="form-control"
                    onChange={handleEvidencias}
                  />
                </div>
              </div>
            </div>

            {/* Cliente */}
            <div className='card-nc'>
              <h5>Información del cliente</h5>
              <div className="form-comprobante-row">
                <div className="mb-2 form-comprobante-field">
                  <label className="form-label">Nombre Cliente<span className="required">*</span></label>
                  <input type="text" placeholder="Nombre del cliente"
                  className={`form-control ${errors.cliente ? 'is-invalid' : ''}`} {...register('cliente')}
                  /> {errors.cliente && <div className="invalid-feedback">{errors.cliente.message}</div>}
                </div>
                <div className="mb-2 form-comprobante-field">
                  <label className="form-label">RUT<span className="required">*</span></label>
                  <input type="text" placeholder="RUT del cliente"
                  className={`form-control ${errors.rut ? 'is-invalid' : ''}`} {...register('rut')}
                  /> {errors.rut && <div className="invalid-feedback">{errors.rut.message}</div>}
                </div>
              </div>
              <div className="form-comprobante-row">
                <div className="mb-2 form-comprobante-field">
                  <label className="form-label">Celular<span className="required">*</span></label>
                  <input type="number"  placeholder="Número de celular"
                  className={`form-control ${errors.celular ? 'is-invalid' : ''}`} {...register('celular')}
                  /> {errors.celular && <div className="invalid-feedback">{errors.celular.message}</div>}
                </div>
                <div className="mb-2 form-comprobante-field">
                  <label className="form-label">Correo</label>
                  <input type="email"placeholder="Correo electrónico"
                    className={`form-control ${errors.correo ? 'is-invalid' : ''}`} {...register('correo')}
                  /> {errors.correo && <div className="invalid-feedback">{errors.correo.message}</div>}
                </div>
              </div>
            </div>

            {/* Firmas */}
            <div className='card-nc'>
              <p className="text-start">Términos y condiciones</p>
              <div className="text-muted pr-4 mb-4" style={{ textAlign: 'justify' }}>
                <small>
                  <ol className='mb-0 ml-3'>
                    <li>La firma proporcionada será utilizada únicamente para la generación del documento oficial en formato PDF y no será almacenada en nuestros sistemas.</li>
                    <li>El taller mecánico no se hace responsable por pérdidas o daños de objetos de valor que permanezcan dentro del vehículo durante su permanencia en las instalaciones.</li>
                    <li>El taller no se hace responsable por daños o fallas que el vehículo ya tuviera antes de ingresar. Estos se dejarán registrados en el informe de recepción.</li>
                    <li>Al firmar este documento, el cliente declara haber leído, comprendido y aceptado los presentes términos y condiciones.</li>
                  </ol>
                </small>
              </div>

              <h5>Firmas de validación</h5>
              <div className="form-comprobante-row">
                <div className="mb-2 signature-field">
                  <label className="form-label">Firma Cliente</label>
                  <FirmaPad sigRef={sigCliente} canvasProps={{ className: 'sigCanvas', height: 150 }}/>
                  <Button type="button" label="Limpiar Firma Cliente" icon="pi pi-eraser" 
                    onClick={() => sigCliente.current?.clear()} className="btn-text"
                  />
                </div>
              </div>
              <div className="form-comprobante-row">
                <div className="mb-2 signature-field">
                  <label className="form-label">Firma Mecánico</label>
                  <FirmaPad sigRef={sigMecanico} canvasProps={{ className: 'sigCanvas', height: 150 }}/>
                  <Button type="button" label="Limpiar Firma Mecánico" icon="pi pi-eraser" 
                    onClick={() => sigMecanico.current?.clear()} className="btn-text"
                  />
                </div>
              </div>
            </div>

            {/* Seccion de botones */}
            <div className='tmc-btns-container'>
              <Permiso modulo="Inventario" permiso="INV_VER_INVENTARIO">
                <Button 
                  type="button" label="Ver Borrador" icon="pi pi-eye" className="inv-btn-cancelar"   
                  onClick={async () => { await crearDocumento('ver'); }}
                />
              </Permiso>

              <Button type="button" label="Guardar" icon="pi pi-save" 
                className="inv-btn-confirm" 
                onClick={handleSubmit(soloGuardar)}
                disabled={isSubmitting}
              />

              {(esRevisor || puedeEnviar) ? (
              <Button type="button" label="Enviar y Guardar" icon="pi pi-send" 
                className="inv-btn-cancelar" 
                onClick={handleSubmit(enviarYGuardar)}
                disabled={isSubmitting}
              />) : null}

              <EstaSeguro
                visible={confirmVisible}
                onHide={() => setConfirmVisible(false)}
                onConfirm={confirmarEnvio}
                titulo="¿Está seguro de enviar el documento?"
                descripcion="El documento PDF que se ha generado será enviado de forma automática al correo del cliente con copia al correo walter.alvarez@transkyrios.com y mauricio.mauriel@transkyrios.com ¿Está seguro de que desea continuar?"
                icon="pi pi-send"
                posicion="center"
              />
            </div>
          </form>
          {/* Continuar con el borrador? */}
          <Continuar restorePrompt={restorePrompt} discard={discard} restore={restore} />
        </section>
      </section>
    </ModuleLayout>
  );
}

export default InformeMantencion;